import React, { createContext, useContext, useState, useCallback } from 'react';
import ToastNotification from '../components/ui/ToastNotification';

const ToastContext = createContext();

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((toast) => {
    const id = Date.now() + Math.random();
    const newToast = { 
      ...toast, 
      id,
      position: toast.position || 'top-right',
      duration: toast.duration !== undefined ? toast.duration : 5000
    };
    setToasts(prev => [...prev, newToast]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showError = useCallback((message, options = {}) => {
    return addToast({
      type: 'error',
      title: options.title || 'Error',
      message,
      duration: options.duration !== undefined ? options.duration : 7000,
      position: options.position || 'top-right',
      actions: options.actions || [],
      ...options
    });
  }, [addToast]);

  const showSuccess = useCallback((message, options = {}) => {
    return addToast({
      type: 'success',
      title: options.title || 'Success',
      message,
      duration: options.duration !== undefined ? options.duration : 3000,
      position: options.position || 'top-right',
      actions: options.actions || [],
      ...options
    });
  }, [addToast]);

  const showWarning = useCallback((message, options = {}) => {
    return addToast({
      type: 'warning',
      title: options.title || 'Warning',
      message,
      duration: options.duration !== undefined ? options.duration : 5000,
      position: options.position || 'top-right',
      actions: options.actions || [],
      ...options
    });
  }, [addToast]);

  const showInfo = useCallback((message, options = {}) => {
    return addToast({
      type: 'info',
      title: options.title || 'Information',
      message,
      duration: options.duration !== undefined ? options.duration : 4000,
      position: options.position || 'top-right',
      actions: options.actions || [],
      ...options
    });
  }, [addToast]);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const contextValue = {
    addToast,
    removeToast,
    showError,
    showSuccess,
    showWarning,
    showInfo,
    clearAllToasts,
    toasts
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {toasts.map(toast => (
        <ToastNotification
          key={toast.id}
          {...toast}
          onClose={removeToast}
        />
      ))}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastContext;
